import { Box, Flex, Image, Text, keyframes } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import useNowPlaying from 'hooks/use-now-playing';
import ScrollingText from 'components/NowPlaying/ScrollingText';
import hanaJam from 'assets/images/emotes/o87Jam.gif';

const Blossom = () => (
  <svg width="22" height="22" viewBox="0 0 40 40" aria-hidden="true">
    {[0, 72, 144, 216, 288].map((rotation) => (
      <ellipse
        key={rotation}
        cx="20"
        cy="11"
        rx="7"
        ry="10"
        transform={`rotate(${rotation} 20 20)`}
        fill="#ffd0e3"
        stroke="#e883ae"
        strokeWidth="1.2"
      />
    ))}
    <circle cx="20" cy="20" r="4" fill="#db6195" />
  </svg>
);

const blossomDrift = keyframes`
  0% { opacity: 0; transform: translate(0, 12px) rotate(-25deg) scale(0.4); }
  18% { opacity: 0.9; }
  55% { opacity: 0.75; transform: translate(var(--drift-mid), -22px) rotate(55deg) scale(1); }
  100% { opacity: 0; transform: translate(var(--drift-end), 16px) rotate(145deg) scale(0.55); }
`;

const songReveal = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const petals = [
  { top: 20, travel: 238, size: 16 },
  { top: 82, travel: 200, size: 12 },
  { top: 46, travel: 258, size: 19 },
  { top: 100, travel: 225, size: 14 },
  { top: 30, travel: 190, size: 12 },
  { top: 70, travel: 245, size: 17 },
];

const SakuraNowPlaying = () => {
  const { songName, singer } = useNowPlaying();
  const songKey = JSON.stringify([songName, singer]);
  const previousSong = useRef(songKey);
  const [transition, setTransition] = useState<string | null>(null);

  useEffect(() => {
    if (previousSong.current === songKey) return;
    previousSong.current = songKey;
    setTransition(songName ? songKey : null);
  }, [songKey, songName]);

  const details = (
    <Box w="100%" minW={0}>
      <ScrollingText
        fontSize="17px"
        fontWeight="700"
        lineHeight="27px"
        color="#a73d6b"
      >
        {songName || 'Waiting for a song'}
      </ScrollingText>
      {singer && (
        <ScrollingText fontSize="12px" lineHeight="21px" color="#875b71">
          {singer}
        </ScrollingText>
      )}
    </Box>
  );

  return (
    <Box
      w="360px"
      h="128px"
      p="10px"
      fontFamily="'Zen Maru Gothic', sans-serif"
      border="1px solid #efa9c7"
      borderRadius="20px"
      bg="#fff4ed"
      backgroundImage="linear-gradient(90deg, #ffc0da80 50%, transparent 50%), linear-gradient(#ffc0da80 50%, transparent 50%)"
      backgroundSize="36px 36px"
      overflow="hidden"
      position="relative"
    >
      <Flex
        h="100%"
        p="12px"
        gap="12px"
        align="center"
        bg="#fffdfd"
        border="1px solid #f7d4e3"
        borderRadius="13px"
      >
        <Box position="relative" flexShrink={0}>
          <Image
            src={hanaJam}
            alt="Hana jamming"
            boxSize="64px"
            objectFit="contain"
            bg="#fff0f6"
            border="2px solid #f3b1cd"
            borderRadius="full"
          />
          <Box position="absolute" right="-4px" bottom="-4px">
            <Blossom />
          </Box>
        </Box>
        <Box flex={1} minW={0} overflow="hidden" whiteSpace="nowrap">
          <Flex align="center" gap="6px" mb="2px" color="#c35688">
            <Text fontSize="11px" fontWeight="700">
              {songName ? 'いまのうた' : 'もうすぐ…'}
            </Text>
            <Box flex={1} borderBottom="2px dotted #f1b8d0" />
            <Text fontSize="9px" letterSpacing="1px" fontWeight="700">
              {songName ? 'NOW PLAYING' : 'STAND BY'}
            </Text>
          </Flex>
          <Box
            key={songKey}
            animation={transition === songKey ? `${songReveal} 450ms ease-out both` : undefined}
            sx={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
          >
            {details}
          </Box>
        </Box>
      </Flex>
      {transition === songKey && (
        <Box
          key={transition}
          position="absolute"
          inset={0}
          pointerEvents="none"
          aria-hidden="true"
          data-blossom-transition="true"
          sx={{ '@media (prefers-reduced-motion: reduce)': { display: 'none' } }}
        >
          {petals.map((petal, index) => (
            <Box
              key={index}
              position="absolute"
              left="66px"
              top={`${petal.top}px`}
              w={`${petal.size}px`}
              h={`${petal.size}px`}
              animation={`${blossomDrift} 1200ms ${index * 75}ms ease-in-out both`}
              onAnimationEnd={index === petals.length - 1 ? () => setTransition(null) : undefined}
              sx={{
                '--drift-mid': `${petal.travel * 0.48}px`,
                '--drift-end': `${petal.travel}px`,
                '& svg': { width: '100%', height: '100%' },
              }}
            >
              <Blossom />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SakuraNowPlaying;
